import { useMemo, useState } from "react";
import { ManufacturingEntityBulkImportModalFrame } from "./ManufacturingEntityBulkImportModalFrame";
import { EXAMPLE_NAMED_VARIANTS_BULK_IMPORT_CSV } from "@/lib/manufacturing/namedVariantsBulkImportExample";
import { buildNamedVariantsBulkImportPreview } from "@/lib/manufacturing/namedVariantsBulkImportPreview";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import { bulkUpsertManufacturingNamedVariants } from "@/lib/manufacturing/namedVariantsClient";
import type { NamedVariantSummary } from "@/lib/manufacturing/namedVariantsClient";

export function ManufacturingNamedVariantsBulkImportModal({
  existingVariants,
  onClose,
  onImported,
}: {
  existingVariants: ReadonlyArray<NamedVariantSummary>;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const preview = useMemo(
    () => buildNamedVariantsBulkImportPreview(text, existingVariants),
    [text, existingVariants],
  );

  const importableCount =
    preview.kind === "ok" ? preview.variants.filter((v) => !v.blocked).length : 0;

  async function handleSubmit() {
    if (preview.kind !== "ok") return;
    const variants = preview.variants
      .filter((v) => !v.blocked)
      .map((v) => ({
        variant_code: v.variant_code,
        variant_name: v.variant_name,
        description: v.description,
        status: v.status,
      }));
    if (variants.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await bulkUpsertManufacturingNamedVariants({ variants });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || "Bulk import failed");
      return;
    }
    onImported();
    onClose();
  }

  return (
    <ManufacturingEntityBulkImportModalFrame
      title="Bulk import named variants (CSV / JSON)"
      formatGuide={
        <>
          <p>
            Header fields only — one row per variant. Upsert matches by{" "}
            <code>variant_code</code> (case-insensitive). Does not change transform config or
            applications on existing variants.
          </p>
          <p>
            Required: <code>variant_code</code>, <code>variant_name</code>. Optional:{" "}
            <code>description</code>, <code>status</code> (draft, active, or archived).
          </p>
        </>
      }
      exampleCsv={EXAMPLE_NAMED_VARIANTS_BULK_IMPORT_CSV}
      onDownloadExample={() =>
        downloadCsv("mfg-named-variants-example.csv", EXAMPLE_NAMED_VARIANTS_BULK_IMPORT_CSV)
      }
      text={text}
      onTextChange={setText}
      onFile={async (file) => setText(await file.text())}
      previewSummary={
        preview.kind === "empty" ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">Paste or upload a file to preview.</p>
        ) : preview.kind === "error" ? (
          <p className="text-sm text-red-700 dark:text-red-300">{preview.error}</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
            <li>
              Create {preview.totals.create} · Update {preview.totals.update} · Blocked{" "}
              {preview.totals.blocked}
            </li>
            {preview.variants
              .filter((v) => v.blocked)
              .slice(0, 5)
              .map((v) => (
                <li key={v.variant_code} className="text-xs text-red-700 dark:text-red-300">
                  {v.variant_code}: {v.blockReason}
                </li>
              ))}
          </ul>
        )
      }
      submitting={submitting}
      submitError={submitError}
      importableCount={importableCount}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    />
  );
}
